import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { SectionHead } from "./SectionHead";
import { Reveal } from "./Reveal";
import { useSection } from "../../lib/content";

const UrgencyBadge = ({ level }) =>
  level === "High" ? (
    <span className="inline-flex items-center gap-1.5 bg-[#FF5050]/10 text-[#E03E3E] font-mono text-[10px] font-bold uppercase tracking-[0.12em] px-3 py-1.5 rounded-full">
      <AlertTriangle className="w-3 h-3" /> High urgency
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 bg-[#2a2a2a] text-slate-300 font-mono text-[10px] font-bold uppercase tracking-[0.12em] px-3 py-1.5 rounded-full">
      <AlertTriangle className="w-3 h-3" /> Medium urgency
    </span>
  );

export const Milestones = () => {
  const c = useSection("milestones");

  return (
    <section id="milestones" className="relative bg-slate-50 py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHead chapter="06" eyebrow={c.eyebrow} title={c.title} lead={c.lead} />

        <Reveal delay={0.15}>
          <div className="mt-14 rounded-2xl border border-[#2a2a2a] bg-[#1a1a1a] overflow-hidden shadow-[0_24px_60px_-30px_rgba(15,23,42,0.25)]">
            <div className="px-7 py-5 border-b border-[#2a2a2a] flex items-center justify-between gap-4 flex-wrap">
              <h3 className="font-display font-bold text-white text-lg tracking-tight">
                {c.tableTitle}
              </h3>
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500">
                Gen 1 devices
              </span>
            </div>
            <div data-testid="platform-milestones-table" className="overflow-x-auto">
              <table className="w-full text-left min-w-[640px]">
                <thead>
                  <tr className="border-b border-[#2a2a2a]">
                    <th className="px-7 py-4 font-mono text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500">Platform</th>
                    <th className="px-7 py-4 font-mono text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500">Gen 1 Milestone</th>
                    <th className="px-7 py-4 font-mono text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {c.rows.map((row) => (
                    <tr key={row.platform} className="border-b border-[#262626] last:border-0 hover:bg-[#232323] transition-colors duration-200">
                      <td className="px-7 py-5 font-display font-bold text-white">{row.platform}</td>
                      <td className="px-7 py-5 text-sm text-slate-400 leading-relaxed max-w-xl">{row.milestone}</td>
                      <td className="px-7 py-5"><UrgencyBadge level={row.urgency} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-12 gap-8 items-center">
          <Reveal className="lg:col-span-5">
            <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-slate-900">
              {c.planTitle}
            </h3>
            <p className="mt-3 text-slate-600 text-sm sm:text-base leading-relaxed">
              {c.planLead}
            </p>
          </Reveal>
          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-4">
            {c.actions.map((text, i) => (
              <Reveal key={text} delay={i * 0.1} className="h-full">
                <div className="h-full flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-5 hover:border-[#024AD8]/50 transition-colors duration-300">
                  <CheckCircle2 className="w-5 h-5 text-[#024AD8] shrink-0 mt-0.5" />
                  <p className="text-sm font-medium text-slate-300 leading-relaxed">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
